import { useState, type ChangeEvent, type FormEvent } from "react";
import { useCurrentBusiness } from "@/features/business/hooks/useCurrentBusiness";
import { Button } from "@/shared/components/shadcn/Button";
import { Input } from "@/shared/components/shadcn/Input";
import { Switch } from "@/shared/components/shadcn/Switch";
import { TextArea } from "@/shared/components/shadcn/TextArea";
import { ErrorState } from "@/shared/components/ui/ErrorState";
import { FieldGroup } from "@/shared/components/ui/FieldGroup";
import { FormPage } from "@/shared/components/ui/FormPage";
import { FormField } from "@/shared/components/ui/FormField";
import { InfoRow } from "@/shared/components/ui/InfoRow";
import { PageHeader } from "@/shared/components/ui/PageHeader";
import { Section } from "@/shared/components/ui/Section";
import { SectionAction } from "@/shared/components/ui/SectionAction";
import type { Service } from "../types/serviceTypes";

export type ServiceFormValues = Pick<Service,
  "name" | "basePrice" | "durationMinutes" | "bufferBeforeMinutes" | "bufferAfterMinutes" | "isActive"
> & { description: string; color: string };

interface ServiceEditProps {
  service: Partial<Service>;
  onSubmit: (data: ServiceFormValues) => void;
  isSubmitting: boolean;
  title: string;
  submitButtonText?: string;
  error?: Error | null;
}

const numericFields = ["basePrice", "durationMinutes", "bufferBeforeMinutes", "bufferAfterMinutes"] as const;

export function ServiceEdit({ service, onSubmit, isSubmitting, title, error }: ServiceEditProps) {
  const { business } = useCurrentBusiness();
  const [formData, setFormData] = useState({
    name: service.name ?? "",
    description: service.description ?? "",
    basePrice: service.basePrice?.toString() ?? "",
    durationMinutes: service.durationMinutes?.toString() ?? "",
    bufferBeforeMinutes: service.bufferBeforeMinutes?.toString() ?? "",
    bufferAfterMinutes: service.bufferAfterMinutes?.toString() ?? "",
    isActive: service.isActive ?? true,
    color: service.color ?? "",
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = event.target;
    setFormData((current) => ({ ...current, [name]: value }));
    setErrors((current) => ({ ...current, [name]: "" }));
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isSubmitting) return;
    const nextErrors: Record<string, string> = {};
    if (!formData.name.trim()) nextErrors.name = "Enter a service name.";
    for (const field of numericFields) {
      const value = formData[field].trim();
      if (!value) continue;
      const number = Number(value);
      if (!Number.isFinite(number)) nextErrors[field] = "Enter a finite number.";
      else if (field === "basePrice" && number < 0) nextErrors[field] = "Price cannot be negative.";
      else if (field !== "basePrice" && (!Number.isInteger(number) || number < (field === "durationMinutes" ? 1 : 0))) {
        nextErrors[field] = field === "durationMinutes" ? "Enter a whole number of at least 1 minute." : "Enter a whole number of 0 or more minutes.";
      }
    }
    setErrors(nextErrors);
    const firstInvalid = Object.keys(nextErrors)[0];
    if (firstInvalid) {
      const control = event.currentTarget.elements.namedItem(firstInvalid);
      if (control instanceof HTMLElement) control.focus();
      return;
    }
    const numberOrNull = (value: string) => value.trim() === "" ? null : Number(value);
    onSubmit({
      name: formData.name.trim(),
      description: formData.description.trim(),
      basePrice: numberOrNull(formData.basePrice),
      durationMinutes: numberOrNull(formData.durationMinutes),
      bufferBeforeMinutes: numberOrNull(formData.bufferBeforeMinutes),
      bufferAfterMinutes: numberOrNull(formData.bufferAfterMinutes),
      isActive: formData.isActive,
      color: formData.color,
    });
  };

  return (
    <FormPage
      header={<PageHeader title={title} />}
      onSubmit={handleSubmit}
      actions={<Button type="submit" size="touch" disabled={isSubmitting}>{isSubmitting ? "Saving..." : "Save"}</Button>}
    >
      {error && <ErrorState message={error.message} />}
      <Section title="Service information">
        <FieldGroup disabled={isSubmitting}>
          <InfoRow
            title="Active"
            caption="Available for booking"
            density="compact"
            actions={<Switch aria-label="Active (available for booking)" name="isActive" checked={formData.isActive} disabled={isSubmitting} onCheckedChange={(checked) => setFormData((current) => ({ ...current, isActive: checked }))} />}
          />
          <FormField id="name" label="Name" required error={errors.name}>
            <Input name="name" value={formData.name} onChange={handleChange} required />
          </FormField>
          <FormField id="description" label="Description">
            <TextArea name="description" value={formData.description} onChange={handleChange} rows={3} />
          </FormField>
          <FormField id="basePrice" label={business ? `Price (${business.currency})` : "Price"} error={errors.basePrice}>
            <Input type="number" inputMode="decimal" name="basePrice" value={formData.basePrice} onChange={handleChange} step="0.01" min="0" />
          </FormField>
        </FieldGroup>
      </Section>
      <Section title="Timing">
        <FieldGroup disabled={isSubmitting}>
          <FormField id="durationMinutes" label="Duration (minutes)" error={errors.durationMinutes}>
            <Input type="number" inputMode="numeric" name="durationMinutes" value={formData.durationMinutes} onChange={handleChange} step="1" min="1" />
          </FormField>
          <div className="grid gap-[18px] sm:grid-cols-2">
            <FormField id="bufferBeforeMinutes" label="Buffer before (minutes)" error={errors.bufferBeforeMinutes}>
              <Input type="number" inputMode="numeric" name="bufferBeforeMinutes" value={formData.bufferBeforeMinutes} onChange={handleChange} step="1" min="0" />
            </FormField>
            <FormField id="bufferAfterMinutes" label="Buffer after (minutes)" error={errors.bufferAfterMinutes}>
              <Input type="number" inputMode="numeric" name="bufferAfterMinutes" value={formData.bufferAfterMinutes} onChange={handleChange} step="1" min="0" />
            </FormField>
          </div>
        </FieldGroup>
      </Section>
      <Section title="Display">
        <FieldGroup disabled={isSubmitting}>
          <FormField id="color" label="Color">
            <Input type="color" name="color" value={formData.color || "#28613d"} onChange={handleChange} className="w-16 cursor-pointer p-1" />
          </FormField>
          <div className="flex items-center gap-3">
            <span className="text-sm text-muted-foreground">{formData.color || "Not set"}</span>
            {formData.color && <SectionAction disabled={isSubmitting} onClick={() => setFormData((current) => ({ ...current, color: "" }))}>Clear color</SectionAction>}
          </div>
        </FieldGroup>
      </Section>
    </FormPage>
  );
}
